/**
 * InvoiceRow Component
 *
 * Displays an invoice in row format using BaseRow layout.
 * Shows invoice number, counterparty, amount, status, and due date.
 */

import React, { useMemo, useState } from 'react';
import { FileText, TrendingUp, TrendingDown } from 'lucide-react';
import { BaseRow } from '../common/base/BaseRow';
import { PrimaryAction, EntityAvatar } from '../common/base';
import { LinkManager } from '../../services/actions/LinkManager';
import { getAvailableActions } from '../common/registry/ActionRegistry';
import { useTranslation } from '../../hooks';
import type { Invoice } from '@business-assistant/shared/domain';

// =============================================================================
// TYPES
// =============================================================================

export interface InvoiceRowProps {
  entity: Invoice;
  onTitleClick?: (invoice: Invoice) => void;
  onActionClick?: (actionType: string, invoiceId: string) => void;
  onAvatarClick?: (name: string, id?: string) => void;
}

// =============================================================================
// COMPONENT
// =============================================================================

export function InvoiceRow({ entity: invoice, onTitleClick, onActionClick, onAvatarClick }: InvoiceRowProps) {
  const { t, language } = useTranslation();
  const locale = language === 'it' ? 'it-IT' : 'en-US';

  // Map Italian status values to translation keys
  const getStatusKey = (status: string): string => {
    const statusMap: Record<string, string> = {
      bozza: 'draft',
      inviato: 'sent',
      pagato: 'paid',
      scaduto: 'overdue',
      annullato: 'cancelled',
    };
    return statusMap[status] || status;
  };

  // Locale-aware date formatting
  const formatDate = (isoDate: string): string => {
    const date = new Date(isoDate);
    return date.toLocaleDateString(locale, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  // Primary action state (favorite)
  const [isFavorite, setIsFavorite] = useState(false);

  // Get linked action counts for icon coloring
  const linkedCounts = useMemo(() => {
    return LinkManager.getLinkedActionCounts(invoice.id);
  }, [invoice.id]);

  // Get available actions for invoices
  const availableActions = useMemo(() => {
    return getAvailableActions(invoice.id);
  }, [invoice.id]);

  // Left slot - Primary Action + Company Avatar
  const leftSlot = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <PrimaryAction
        type="star"
        active={isFavorite}
        onChange={(favorite) => {
          setIsFavorite(favorite);
          onActionClick?.('favorite', invoice.id);
        }}
      />
      <EntityAvatar
        items={{ id: invoice.counterpartyId, name: invoice.counterparty }}
        type="company"
        size={24}
        onClick={onAvatarClick ? (item) => onAvatarClick(item.name, item.id) : undefined}
      />
    </div>
  );

  // Build title
  const title = `${invoice.number} - ${invoice.counterparty}`;

  // Build meta content
  const metaContent = useMemo(() => {
    const parts: React.ReactNode[] = [];

    // Amount with direction
    const amount = (invoice.total / 100).toFixed(2);
    parts.push(
      <span
        key="amount"
        style={{
          ...styles.amount,
          color: invoice.direction === 'in' ? '#16a34a' : '#dc2626',
        }}
      >
        {invoice.direction === 'in' ? '+' : '-'}€{amount}
      </span>
    );

    // Status
    parts.push(
      <span
        key="status"
        style={{
          ...styles.statusBadge,
          ...getStatusStyle(invoice.status),
        }}
      >
        {t(`accounting.invoiceStatus.${getStatusKey(invoice.status)}`)}
      </span>
    );

    // Due date
    if (invoice.dueAt) {
      const isOverdue =
        invoice.status !== 'pagato' &&
        invoice.status !== 'annullato' &&
        new Date(invoice.dueAt) < new Date();

      parts.push(
        <span
          key="due"
          style={{
            ...styles.dueDate,
            ...(isOverdue ? styles.overdue : {}),
          }}
        >
          {t('invoices.duePrefix')} {formatDate(invoice.dueAt)}
        </span>
      );
    }

    // Invoice date
    parts.push(
      <span key="date" style={styles.date}>
        {formatDate(invoice.at)}
      </span>
    );

    return (
      <div style={styles.metaContainer}>
        {parts.map((part, idx) => (
          <React.Fragment key={idx}>
            {idx > 0 && <span style={styles.separator}>·</span>}
            {part}
          </React.Fragment>
        ))}
      </div>
    );
  }, [invoice, t, getStatusKey, formatDate]);

  // Icon based on direction
  const icon = useMemo(() => {
    if (invoice.direction === 'in') {
      return <TrendingUp size={16} style={{ color: '#16a34a' }} />;
    } else {
      return <TrendingDown size={16} style={{ color: '#dc2626' }} />;
    }
  }, [invoice.direction]);

  return (
    <BaseRow
      entity={{ ...invoice, title }}
      entityType="invoice"
      leftSlot={leftSlot}
      icon={icon}
      metaContent={metaContent}
      availableActions={availableActions}
      linkedActionCounts={linkedCounts}
      onTitleClick={onTitleClick}
      onActionClick={onActionClick}
      done={invoice.status === 'pagato' || invoice.status === 'annullato'}
    />
  );
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

// getStatusLabel is now handled by translation t(`accounting.invoiceStatus.${key}`)

function getStatusStyle(status: string): React.CSSProperties {
  const statusStyles: Record<string, React.CSSProperties> = {
    bozza: { background: '#f3f4f6', color: '#6b7280' },
    inviato: { background: '#dbeafe', color: '#1e40af' },
    pagato: { background: '#dcfce7', color: '#16a34a' },
    scaduto: { background: '#fef2f2', color: '#dc2626' },
    annullato: { background: '#f3f4f6', color: '#9ca3af' },
  };
  return statusStyles[status] || statusStyles.bozza;
}

// formatDate is now handled inside the component with locale awareness

// =============================================================================
// STYLES
// =============================================================================

const styles = {
  metaContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
  } as React.CSSProperties,
  separator: {
    color: '#ccc',
    margin: '0 2px',
  } as React.CSSProperties,
  amount: {
    fontWeight: 600,
    fontSize: '13px',
  } as React.CSSProperties,
  statusBadge: {
    padding: '2px 6px',
    borderRadius: '3px',
    fontSize: '11px',
    fontWeight: 500,
  } as React.CSSProperties,
  dueDate: {
    color: '#666',
  } as React.CSSProperties,
  overdue: {
    color: '#dc2626',
    fontWeight: 500,
  } as React.CSSProperties,
  date: {
    color: '#999',
  } as React.CSSProperties,
};
